import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const PermissionsContext = createContext({
  permissions: {}, role: "", roleId: null, roleName: null, roleCode: null,
  isPdg: false, can: () => false, isLoading: true, refresh: () => {},
});

// Broadcast helper: outros módulos (Users, Roles, RBAC editor) devem chamar isso
// depois de alterar permissões/role/atribuição para invalidar o cache local.
// Mesmo mecanismo já usado por AP-021 Favorites (window CustomEvent).
export const invalidatePermissionsCache = () => {
  try { window.dispatchEvent(new CustomEvent("jms:permissions-invalidate")); } catch {}
};

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState("");
  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [isPdg, setIsPdg] = useState(false);
  const [isLoading, setLoading] = useState(true);
  // AP-035 · Discount limits injected by /auth/permissions. Unlimited by default.
  const [discountLimits, setDiscountLimits] = useState({ can_grant: true, max_amount: null, max_percent: null });
  const [tick, setTick] = useState(0);
  const lastFetchAt = useRef(0);

  // Fonte única de verdade — sempre GET /auth/permissions (mesmo endpoint dos outros módulos).
  const fetchPerms = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/auth/permissions");
      setPermissions(data.permissions || {});
      setRole(data.role || "");
      setRoleId(data.role_id || null);
      setRoleName(data.role_name || null);
      setRoleCode(data.role_code || null);
      setIsPdg(!!data.is_pdg);
      setDiscountLimits(data.discount_limits || { can_grant: true, max_amount: null, max_percent: null });
      lastFetchAt.current = Date.now();
    } catch {
      setPermissions({}); setRole(""); setRoleId(null);
      setRoleName(null); setRoleCode(null); setIsPdg(false);
      setDiscountLimits({ can_grant: true, max_amount: null, max_percent: null });
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh explícito: qualquer componente/callsite pode chamar via context.
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  // Primeira carga + qualquer mudança de user + qualquer tick de refresh.
  useEffect(() => {
    if (!user) {
      setPermissions({}); setRole(""); setRoleId(null);
      setRoleName(null); setRoleCode(null); setIsPdg(false);
      setDiscountLimits({ can_grant: true, max_amount: null, max_percent: null });
      setLoading(false);
      return;
    }
    fetchPerms(false);
  }, [user, tick, fetchPerms]);

  // AP-060 · Invalidação automática do cache de permissões.
  //   1. `focus`   → usuário voltou para a aba (janela reativada)
  //   2. `visibilitychange` → aba voltou a ser visível
  //   3. `jms:permissions-invalidate` → outro módulo pediu recarregar (após admin
  //      alterar role/permissões via Users, Roles, RBAC editor, etc.)
  //   4. `storage` → outra aba do mesmo usuário sinalizou mudança.
  // Throttle: no máximo 1 refetch a cada 3s para não sobrecarregar o backend.
  useEffect(() => {
    if (!user) return;
    const THROTTLE_MS = 3000;
    const maybeRefetch = () => {
      if (Date.now() - lastFetchAt.current < THROTTLE_MS) return;
      fetchPerms(true);
    };
    const onVisibility = () => { if (document.visibilityState === "visible") maybeRefetch(); };
    const onStorage = (e) => { if (e.key === "jms.permissions.bump") maybeRefetch(); };
    window.addEventListener("focus", maybeRefetch);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("jms:permissions-invalidate", maybeRefetch);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", maybeRefetch);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("jms:permissions-invalidate", maybeRefetch);
      window.removeEventListener("storage", onStorage);
    };
  }, [user, fetchPerms]);

  // AP-098 · Master Super Admin bypass mirroring backend `has_perm`. Ensures
  // the Sidebar & pages render for the super-admin even when the /auth/permissions
  // response has not yet loaded or when a newly added module has no explicit grant.
  const can = (module, action) => {
    if (role === "master_super_admin") return true;
    return Array.isArray(permissions[module]) && permissions[module].includes(action);
  };

  return (
    <PermissionsContext.Provider value={{
      permissions, role, roleId, roleName, roleCode, isPdg, can, isLoading, refresh,
      discountLimits,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export const usePermissions = () => useContext(PermissionsContext);

export const ROLE_LABELS = {
  master_super_admin: "Master Super Admin",
  super_admin: "Super Admin",
  director: "Diretor(a)",
  admin: "Admin",
  secretary: "Secretário(a)",
  finance: "Financeiro",
  lawyer: "Setor de Documentação",
  employee: "Funcionário(a)",
  client: "Cliente",
};
